import { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import { cx } from '@linaria/core';
import { grpcClient, GRPCClientProvider } from 'api/grpc/client';
import { Concept } from 'api/grpc/resources';
import { selectModelTypeFieldTypeInfo, DATASET_VERSION_ID_PATH } from 'modules/Models/components/ModelForm/utils/utils';
import { formContainer, inputContainer } from 'modules/Models/components/ModelForm/styles';
import { FormikInitialValues } from 'modules/Models/components/ModelForm/utils/types';
import { Input } from 'components/Input';
import { queryKeys, useQueryTE } from 'utils/react-query';
import { either, pipe, errorToReactLeft } from 'utils/fp';
import { fetchModelVersionOutputInfoTE } from 'api/models/fetchModelVersionOutputInfo';
import { useRouter } from 'next/router';
import _get from 'lodash/get';
import { FormLabel } from '../FormLabel';
import { Concepts } from './Concepts/Concepts';
import { conceptOuterContainer, EmptyInputPlaceholder } from './ConceptFormElement.styles';

type ConceptFormElementProps = React.InputHTMLAttributes<HTMLInputElement> & { path: string };

export const ConceptFormElement = ({ path }: ConceptFormElementProps) => {
  const { values, setFieldValue } = useFormikContext();
  const { fieldValueMap } = values as FormikInitialValues;
  const { description } = selectModelTypeFieldTypeInfo(path, values as FormikInitialValues);
  const [selectedConcepts, setSelectedConcepts] = useState<Concept[]>([]); // keep
  const selectedDatasetVersionId = (values as FormikInitialValues).fieldValueMap[DATASET_VERSION_ID_PATH] as string;
  const { userOrOrgId, appId, modelId } = useRouter().query as { userOrOrgId: string; appId: string; modelId: string };

  useEffect(() => {
    // properties on concept object are filtered because payload expects only id and name properties
    // otherwise the post request returns error - 'invalid field in the list of values'
    const cleanedValues = selectedConcepts.length ? selectedConcepts.map((concept) => ({ id: concept.getId(), name: concept.getName() })) : [];
    setFieldValue('fieldValueMap', { ...fieldValueMap, [path]: cleanedValues });
  }, [selectedConcepts]);

  // setting default value on mount and on versionId change
  const preselectedConceptsList = usePreselectedDatasetVersionConcepts({ userOrOrgId, appId, modelId });
  useEffect(() => {
    setSelectedConcepts(preselectedConceptsList);
  }, [preselectedConceptsList]);

  return (
    <div className={formContainer}>
      <div className={inputContainer}>
        <FormLabel path={path} description={description as string} required />
        <div className={conceptOuterContainer}>
          <GRPCClientProvider client={grpcClient}>
            {selectedDatasetVersionId ? (
              <Concepts selectedConcepts={selectedConcepts} setSelectedConcepts={setSelectedConcepts} />
            ) : (
              <Input role="textbox" className={cx(EmptyInputPlaceholder, 'disabled')} placeholder="Select Dataset Version" />
            )}
          </GRPCClientProvider>
        </div>
      </div>
    </div>
  );
};

type UsePreselectedDatasetVersionConceptsProps = { modelId: string; appId: string; userOrOrgId: string };

const usePreselectedDatasetVersionConcepts = ({ userOrOrgId, appId, modelId }: UsePreselectedDatasetVersionConceptsProps) => {
  const [conceptsList, setConceptsList] = useState<Concept[]>([]);
  const { data } = useQueryTE(
    [
      queryKeys.DatasetVersionConcepts,
      {
        appId,
        userOrOrgId,
        modelId,
      },
    ],
    fetchModelVersionOutputInfoTE(
      {
        appId,
        userId: userOrOrgId,
        modelId,
      },
      errorToReactLeft,
    ),
    {
      enabled: Boolean(userOrOrgId) && Boolean(modelId),
    },
  );

  useEffect(() => {
    if (data) {
      pipe(
        data,
        either.fold(
          () => {},
          (resp) => {
            const concepts = _get(resp, 'model.model_version.output_info.data.concepts', []).map((concept: CF.API.Concept) => {
              const newConcept = new Concept();
              newConcept.setId(concept.id);
              newConcept.setName(concept.name);
              return newConcept;
            });
            setConceptsList(concepts);
          },
        ),
      );
    }
  }, [data]);

  return conceptsList;
};
